/**
 * Trend series + per-car totals, ported from Android (FuelViewModel). Segments
 * are computed between consecutive same-type entries (fuel↔fuel, charge↔charge),
 * newest 12 kept — matching the phone dashboards.
 */
import type { Car, FuelLog } from "../model.ts";
import type { Economy } from "./economy.ts";

export interface TrendPoint {
  date: number | null; // epoch seconds
  value: number;
}

const byDate = (logs: FuelLog[]) => [...logs].sort((a, b) => (a.date ?? 0) - (b.date ?? 0));
const qtyOf = (l: FuelLog) => l.kwhAdded ?? l.volumeAdded;
const isElec = (l: FuelLog) => l.kwhAdded != null;

/** Same-type consumption per segment, in the active economy unit. */
export function consumptionSegments(logs: FuelLog[], economy: Economy): TrendPoint[] {
  const ord = byDate(logs);
  const out: TrendPoint[] = [];
  for (let i = 1; i < ord.length; i++) {
    const prev = ord[i - 1]!, curr = ord[i]!;
    if (isElec(prev) !== isElec(curr)) continue;
    const v = economy.consumptionValue(curr.odometer - prev.odometer, qtyOf(curr), isElec(curr));
    if (v == null) continue;
    out.push({ date: curr.date ?? null, value: v });
  }
  return out.slice(-12);
}

/** Cost per display-distance unit per segment (converted cost ÷ distance). */
export function costPerDistanceSegments(logs: FuelLog[], economy: Economy): TrendPoint[] {
  const ord = byDate(logs);
  const out: TrendPoint[] = [];
  for (let i = 1; i < ord.length; i++) {
    const d = ord[i]!.odometer - ord[i - 1]!.odometer;
    if (d <= 0) continue;
    out.push({ date: ord[i]!.date ?? null, value: ord[i]!.totalCost / economy.convertDistance(d) });
  }
  return out.slice(-12);
}

export interface FuelTotals {
  count: number;
  fuelCount: number;
  chargeCount: number;
  totalVolumeDisplay: number; // display volume (L or gal)
  totalEnergy: number; // kWh
  totalCost: number;
}

export function fuelTotals(logs: FuelLog[], economy: Economy): FuelTotals {
  let vol = 0, energy = 0, cost = 0, fuelCount = 0, chargeCount = 0;
  for (const l of logs) {
    vol += l.volumeAdded;
    energy += l.kwhAdded ?? 0;
    cost += l.totalCost;
    if (l.volumeAdded > 0) fuelCount++;
    if ((l.kwhAdded ?? 0) > 0) chargeCount++;
  }
  return {
    count: logs.length,
    fuelCount,
    chargeCount,
    totalVolumeDisplay: economy.convertVolume(vol),
    totalEnergy: energy,
    totalCost: cost,
  };
}

export const fuelLogsForCar = (logs: FuelLog[], carId: string) => logs.filter((l) => l.carId === carId);

// ── Full trend series (Insights chooser) ─────────────────────────────────────
// 1:1 port of Android `FuelViewModel.trendSeriesList`, including the PHEV split
// (fuel vs electric economy/price), combined cost, distance total, and solar.
// Colours are semantic tokens the UI maps to CSS vars (core stays UI-agnostic).
// Web is single-currency, so Android's converted* helpers collapse to raw values.
export type TrendColor = "amber" | "amberHot" | "cyan" | "redline" | "lime";

export interface TrendSeries {
  title: string;
  unit: string;
  color: TrendColor;
  points: TrendPoint[];
  betterIsLower: boolean;
  minLabel: string;
  maxLabel: string;
  /** Optional headline total shown as its own box in the detail (e.g. total distance). */
  totalLabel?: string;
  totalValue?: number | null;
  /** Optional highlight line (e.g. solar savings). */
  extraInfo?: string;
}

const ascByDate = (logs: FuelLog[]) => [...logs].sort((a, b) => (a.date ?? 0) - (b.date ?? 0));

/** Same-type economy segments, filtered to one entry kind, newest 12. */
function economySegments(logs: FuelLog[], economy: Economy, electric: boolean): TrendPoint[] {
  const ord = ascByDate(logs).filter((l) => (electric ? (l.kwhAdded ?? 0) > 0 : l.volumeAdded > 0));
  const out: TrendPoint[] = [];
  for (let i = 1; i < ord.length; i++) {
    const qty = electric ? (ord[i]!.kwhAdded ?? 0) : ord[i]!.volumeAdded;
    const v = economy.consumptionValue(ord[i]!.odometer - ord[i - 1]!.odometer, qty, electric);
    if (v == null) continue;
    out.push({ date: ord[i]!.date ?? null, value: v });
  }
  return out.slice(-12);
}

/** All available fuel/energy trends for a car, newest 12 points each. */
export function buildFuelTrends(logs: FuelLog[], economy: Economy, currency: string, car: Car): TrendSeries[] {
  const ft = (car.fuelType ?? "").toLowerCase();
  const isEV = ft === "electric";
  const isPHEV = ft === "plug-in hybrid";
  const ord = ascByDate(logs);
  const out: TrendSeries[] = [];
  const better = economy.betterIsLower(isEV);
  const fuelBetter = economy.betterIsLower(false);
  const electricBetter = economy.betterIsLower(true);
  const vLabel = economy.volumeLabel();
  const dLabel = economy.distanceLabel();

  // 1. Economy trend(s)
  if (isPHEV) {
    out.push({
      title: "FUEL ECONOMY TREND", unit: `${vLabel}/100${dLabel}`, color: "amber",
      points: economySegments(logs, economy, false), betterIsLower: fuelBetter,
      minLabel: fuelBetter ? "BEST" : "WORST", maxLabel: fuelBetter ? "WORST" : "BEST",
    });
    out.push({
      title: "ELECTRIC ECONOMY TREND", unit: economy.energyEfficiencyLabel(), color: "cyan",
      points: economySegments(logs, economy, true), betterIsLower: electricBetter,
      minLabel: "BEST", maxLabel: "WORST",
    });
  } else {
    out.push({
      title: "AVG CONSUMPTION TREND", unit: economy.economyLabel(isEV), color: "amber",
      points: consumptionSegments(logs, economy), betterIsLower: better,
      minLabel: better ? "BEST" : "WORST", maxLabel: better ? "WORST" : "BEST",
    });
  }

  // 2. Unit price
  const fuelPrice: TrendPoint[] = [];
  const energyPrice: TrendPoint[] = [];
  for (const l of ord) {
    if (l.volumeAdded > 0) {
      const cost = l.fuelCost ?? l.totalCost;
      fuelPrice.push({ date: l.date ?? null, value: cost / economy.convertVolume(l.volumeAdded) });
    }
    if ((l.kwhAdded ?? 0) > 0) {
      const cost = l.energyCost ?? l.totalCost;
      energyPrice.push({ date: l.date ?? null, value: cost / (l.kwhAdded ?? 1) });
    }
  }
  if (isPHEV) {
    out.push({ title: `FUEL PRICE PER ${vLabel.toUpperCase()}`, unit: `${currency}/${vLabel}`, color: "cyan", points: fuelPrice.slice(-12), betterIsLower: true, minLabel: "LOW", maxLabel: "HIGH" });
    out.push({ title: "ENERGY PRICE PER KWH", unit: `${currency}/kWh`, color: "cyan", points: energyPrice.slice(-12), betterIsLower: true, minLabel: "LOW", maxLabel: "HIGH" });
  } else {
    out.push({
      title: `PRICE PER ${isEV ? "KWH" : vLabel.toUpperCase()}`, unit: `${currency}/${isEV ? "kWh" : vLabel}`,
      color: "cyan", points: (isEV ? energyPrice : fuelPrice).slice(-12), betterIsLower: true, minLabel: "LOW", maxLabel: "HIGH",
    });
  }

  // 3. Cost per entry
  out.push({
    title: `COST PER ${isPHEV ? "ENTRY" : isEV ? "CHARGE" : "FILL-UP"}`, unit: currency, color: "redline",
    points: ord.map((l) => ({ date: l.date ?? null, value: l.totalCost })).slice(-12),
    betterIsLower: true, minLabel: "LOW", maxLabel: "HIGH",
  });

  // 4. Cost per distance
  out.push({
    title: `COST PER ${dLabel.toUpperCase()}`, unit: `${currency}/${dLabel}`, color: "amberHot",
    points: costPerDistanceSegments(logs, economy), betterIsLower: true, minLabel: "LOW", maxLabel: "HIGH",
  });

  // 5. Distance between entries (+ total distance owned)
  const dist: TrendPoint[] = [];
  for (let i = 1; i < ord.length; i++) {
    const d = ord[i]!.odometer - ord[i - 1]!.odometer;
    if (d > 0) dist.push({ date: ord[i]!.date ?? null, value: economy.convertDistance(d) });
  }
  out.push({
    title: `DISTANCE BETWEEN ${isPHEV ? "ENTRIES" : isEV ? "CHARGES" : "FILL-UPS"}`, unit: dLabel, color: "lime",
    points: dist.slice(-12), betterIsLower: false, minLabel: "SHORT", maxLabel: "LONG",
    totalLabel: "TOTAL", totalValue: totalDistanceOwned(logs, economy, car),
  });

  // 6. Volume / energy per entry (+ PHEV combined cost)
  if (isPHEV) {
    out.push({
      title: "VOLUME PER FILL-UP", unit: vLabel, color: "amberHot",
      points: ord.filter((l) => l.volumeAdded > 0).map((l) => ({ date: l.date ?? null, value: economy.convertVolume(l.volumeAdded) })).slice(-12),
      betterIsLower: false, minLabel: "MIN", maxLabel: "MAX",
    });
    out.push({
      title: "ENERGY PER CHARGE", unit: "kWh", color: "amberHot",
      points: ord.filter((l) => (l.kwhAdded ?? 0) > 0).map((l) => ({ date: l.date ?? null, value: l.kwhAdded ?? 0 })).slice(-12),
      betterIsLower: false, minLabel: "MIN", maxLabel: "MAX",
    });
    const combined: TrendPoint[] = [];
    let totCost = 0;
    for (const l of ord) totCost += l.totalCost;
    for (let i = 1; i < ord.length; i++) {
      const d = ord[i]!.odometer - ord[i - 1]!.odometer;
      if (d <= 0) continue;
      combined.push({ date: ord[i]!.date ?? null, value: (ord[i]!.totalCost / economy.convertDistance(d)) * 100 });
    }
    const totDistKm = ord.reduce((s, l, i) => (i === 0 ? 0 : s + Math.max(0, l.odometer - ord[i - 1]!.odometer)), 0);
    const avgCombined = combined.length && totDistKm > 0 ? (totCost / economy.convertDistance(totDistKm)) * 100 : null;
    out.push({
      title: `COMBINED COST PER 100${dLabel.toUpperCase()}`, unit: `${currency}/100${dLabel}`, color: "cyan",
      points: combined.slice(-12), betterIsLower: true, minLabel: "LOW", maxLabel: "HIGH",
      totalLabel: "AVG", totalValue: avgCombined,
    });
  } else {
    out.push({
      title: isEV ? "ENERGY PER CHARGE" : "VOLUME PER FILL-UP", unit: isEV ? "kWh" : vLabel, color: "amberHot",
      points: ord
        .filter((l) => (isEV ? (l.kwhAdded ?? 0) > 0 : l.volumeAdded > 0))
        .map((l) => ({ date: l.date ?? null, value: isEV ? (l.kwhAdded ?? 0) : economy.convertVolume(l.volumeAdded) }))
        .slice(-12),
      betterIsLower: false, minLabel: "MIN", maxLabel: "MAX",
    });
  }

  // 7. Solar charging (only if any solar recorded)
  const solar = ord.filter((l) => (l.solarKwh ?? 0) > 0).map((l) => ({ date: l.date ?? null, value: l.solarKwh ?? 0 }));
  if (solar.length) {
    const savings = logs.reduce((s, l) => s + (((l.solarKwh ?? 0) > 0 && (l.solarPrice ?? 0) > 0) ? (l.solarKwh ?? 0) * (l.solarPrice ?? 0) : 0), 0);
    out.push({
      title: "SOLAR CHARGING", unit: "kWh", color: "lime", points: solar.slice(-12),
      betterIsLower: false, minLabel: "MIN", maxLabel: "MAX",
      extraInfo: savings > 0 ? `SAVED · ${Math.round(savings)} ${currency}` : undefined,
    });
  }

  // Hide trends with no data (e.g. a PHEV with only charges has no petrol trend).
  return out.filter((s) => s.points.length > 0);
}

/** Current odometer − odometer at purchase (falls back to first logged odo). */
function totalDistanceOwned(logs: FuelLog[], economy: Economy, car: Car): number | null {
  if (logs.length === 0) return null;
  const max = logs.reduce((m, l) => Math.max(m, l.odometer), 0);
  if (max <= 0) return null;
  const baseline = car.purchaseOdometer && car.purchaseOdometer > 0
    ? car.purchaseOdometer
    : logs.reduce((m, l) => Math.min(m, l.odometer), Infinity);
  return economy.convertDistance(Math.max(0, max - baseline));
}
